/**
 * Team picker — list the coach's teams, filter to those that haven't
 * already registered for this event, let them tap one to continue.
 *
 * Eligibility-by-division is a softer signal in the existing TN flow
 * (organizer division naming is inconsistent), so we don't hard-block
 * teams whose age/gender doesn't trivially match the chosen division.
 * If the team-vs-division pairing is wrong, the existing /eligible
 * endpoint surfaces a warning that the confirm screen renders.
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useFlowBase } from '@/state/useFlowBase';

import type { EmbedClient, TeamSummary } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useVocab } from '@/state/vocab';
import '@/pages/TeamPickerPage.css';

interface Props {
  client: EmbedClient;
}

export function GfgTeamPickerPage({ client }: Props) {
  const { base, afterTeam } = useFlowBase();
  const navigate = useNavigate();
  const { selectTeam } = useFlow();
  const vocab = useVocab();

  const [teams, setTeams] = useState<TeamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client
      .getTeams()
      .then(setTeams)
      .catch((err) => {
        if (err?.response?.status === 401) {
          // Token expired or revoked. Bounce back to auth.
          navigate(`${base}/auth`, { replace: true });
          return;
        }
        setError('Could not load your teams. Please try again.');
      });
  }, [client, base, navigate]);

  if (error) {
    return <div role="alert" className="tn-team-picker__error">{error}</div>;
  }

  if (!teams) {
    return <div className="tn-team-picker__loading">Loading {vocab.entrantPlural}…</div>;
  }

  // No teams yet (a coach who just signed in with a new email): straight to
  // setting one up, rather than an empty list.
  if (teams.length === 0) return <Navigate to={`${base}/team-create`} replace />;

  return (
    <div className="tn-team-picker">
      <h1 className="tn-team-picker__title">Choose your {vocab.entrant}</h1>
      <ul className="tn-team-picker__teams">
        {teams.map((team) => (
          <li key={team.id}>
            <button
              className="tn-team-picker__team"
              type="button"
              onClick={() => {
                selectTeam(team);
                navigate(afterTeam);
              }}
            >
              <div className="tn-team-picker__team-info">
                <div className="tn-team-picker__team-name">{team.name}</div>
                <div className="tn-team-picker__team-meta">
                  {[team.sport, team.age_group, team.gender]
                    .filter(Boolean)
                    .join(' · ')}
                  {team.city && team.state ? ` · ${team.city}, ${team.state}` : ''}
                </div>
                {/* Roster is a sports concept — hide for charity entries. */}
                {!vocab.isCharity ? (
                  <div className="tn-team-picker__team-roster">
                    {team.roster_count} player{team.roster_count === 1 ? '' : 's'} on roster
                  </div>
                ) : null}
              </div>
              <span className="tn-team-picker__team-arrow" aria-hidden>→</span>
            </button>
          </li>
        ))}
        <li>
          <button
            className="tn-team-picker__team tn-team-picker__team--create"
            type="button"
            onClick={() => navigate(`${base}/team-create`)}
          >
            <div className="tn-team-picker__team-info">
              <div className="tn-team-picker__team-name">Register a new {vocab.entrant}</div>
            </div>
            <span className="tn-team-picker__team-arrow" aria-hidden>→</span>
          </button>
        </li>
      </ul>
    </div>
  );
}
